<template>
  <ServicePage
    service-id="storage"
    :monthly-cost="monthlyCost"
    :item-count="lines.length"
    :can-add="canAdd"
    @add="addLines"
  >
    <div class="space-y-6">
      <!-- S3 standard -->
      <ConfigSection
        section-key="s3standard"
        title="S3 Standard"
        subtitle="Object storage with per-request charges"
        v-model:enabled="standard.enabled"
        :monthly-cost="cost(standardLine)"
      >
        <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
          <NumberField
            v-model="standard.storage"
            label="Storage"
            unit="GB/month"
            :hint="hint('storage.s3.standard', 'storage')"
          />
          <NumberField
            v-model="standard.write"
            label="Write operations"
            unit="requests/month"
            :hint="hint('storage.s3.standard', 'write')"
          />
          <NumberField
            v-model="standard.read"
            label="Read operations"
            unit="requests/month"
            :hint="hint('storage.s3.standard', 'read')"
          />
        </div>
      </ConfigSection>

      <!-- S3 backup -->
      <ConfigSection
        section-key="s3backup"
        title="S3 Backup"
        subtitle="Backup storage with optional disaster recovery copies"
        v-model:enabled="backup.enabled"
        :monthly-cost="cost(backupLine)"
      >
        <div class="space-y-5">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <NumberField
              v-model="backup.storage"
              label="Backup storage"
              unit="TB/month"
              :hint="hint('storage.s3.backup', 'storage')"
            />
            <NumberField
              v-if="backup.dr"
              v-model="backup.drAmount"
              label="Disaster recovery backup"
              unit="GB/month"
              :hint="hint('storage.s3.backup', 'drbackup')"
            />
          </div>
          <label class="flex items-center gap-3 text-sm text-slate-700">
            <input type="checkbox" v-model="backup.dr" class="w-4 h-4 rounded border-slate-300 text-indigo-600" />
            <span>Add disaster-recovery ready backup (copy in a second datacenter plus LTO)</span>
          </label>
        </div>
      </ConfigSection>

      <!-- LTO -->
      <ConfigSection
        section-key="lto"
        title="LTO archive"
        subtitle="Long-term tape archival"
        v-model:enabled="lto.enabled"
        :monthly-cost="cost(ltoLine)"
      >
        <div class="space-y-5">
          <PriceTable :entries="ltoPlans" selectable :selected="lto.plan" @select="lto.plan = $event" />
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <NumberField
              v-model="lto.storage"
              label="Archived data"
              unit="TB"
              :hint="ltoStorageHint"
            />
            <NumberField
              v-model="lto.retrieval"
              label="Data retrieved per month"
              unit="GB"
              :hint="hint('storage.lto', 'fileretrieval')"
            />
          </div>
          <p v-if="lto.plan" class="text-xs text-slate-500">{{ lto.plan.description }}</p>
        </div>
      </ConfigSection>

      <!-- Block storage -->
      <ConfigSection
        section-key="block"
        title="Block storage"
        subtitle="Persistent volumes attached to instances"
        v-model:enabled="block.enabled"
        :monthly-cost="cost(blockLine)"
      >
        <NumberField
          v-model="block.storage"
          label="Block storage"
          unit="GB/month"
          :hint="hint('storage.block', 'standard')"
        />
      </ConfigSection>

      <!-- Container registry -->
      <ConfigSection
        section-key="registry"
        title="Container registry"
        subtitle="Storage for your container images"
        v-model:enabled="registry.enabled"
        :monthly-cost="cost(registryLine)"
      >
        <NumberField
          v-model="registry.storage"
          label="Registry storage"
          unit="GB/month"
          :hint="hint('storage.containerRegistry', 'standard')"
        />
      </ConfigSection>

      <!-- EFS -->
      <ConfigSection
        section-key="efs"
        title="Elastic file system"
        subtitle="Shared file storage, individually quoted"
        v-model:enabled="efs.enabled"
        :monthly-cost="0"
        :on-request="true"
      >
        <p class="text-sm text-indigo-800 bg-indigo-50 border border-indigo-200 rounded p-3">
          {{ efsEntry?.description }}
        </p>
      </ConfigSection>

      <CostPreview v-if="lines.length" :lines-prop="lines" />
    </div>
  </ServicePage>
</template>

<script>
import { mapGetters } from "vuex";
import ServicePage from "@/components/config/ServicePage.vue";
import ConfigSection from "@/components/config/ConfigSection.vue";
import NumberField from "@/components/config/NumberField.vue";
import PriceTable from "@/components/config/PriceTable.vue";
import CostPreview from "@/components/config/CostPreview.vue";
import { configurable } from "@/mixins/configurable";
import { byType, estimateLine, lineItem, priceOf, round } from "@/utils/pricing";

// The WORM volume is sold in 12 TB increments.
const WORM_INCREMENT_TB = 12;

export default {
  name: "StorageService",
  components: { ServicePage, ConfigSection, NumberField, PriceTable, CostPreview },
  mixins: [configurable],
  data() {
    return {
      standard: { enabled: true, storage: 0, write: 0, read: 0 },
      backup: { enabled: false, storage: 0, dr: false, drAmount: 0 },
      lto: { enabled: false, plan: null, storage: 0, retrieval: 0 },
      block: { enabled: false, storage: 0 },
      registry: { enabled: false, storage: 0 },
      efs: { enabled: false },
    };
  },
  computed: {
    ...mapGetters({ section: "pricing/section" }),

    /** The LTO list mixes storage tiers with the retrieval charge. */
    ltoPlans() {
      return (this.section("storage.lto") || []).filter(
        (entry) => entry.type !== "fileretrieval"
      );
    },
    efsEntry() {
      return (this.section("storage.elasticFileSystem") || [])[0];
    },
    ltoStorageHint() {
      if (this.lto.plan?.type === "worm") {
        return `Billed in ${WORM_INCREMENT_TB} TB increments.`;
      }
      return "Billed per TB per month.";
    },

    standardLine() {
      const { enabled, storage, write, read } = this.standard;
      if (!enabled) return null;

      const entries = this.section("storage.s3.standard");
      const items = [];

      if (storage > 0) {
        items.push(
          lineItem({
            label: "Object storage",
            detail: `${round(storage)} GB`,
            quantity: storage,
            unit: "GB/month",
            unitPrice: priceOf(byType(entries, "storage")),
          })
        );
      }
      if (write > 0) {
        items.push(
          lineItem({
            label: "Write operations",
            detail: `${Number(write).toLocaleString()} requests`,
            quantity: write / 1000,
            unit: "kOPS",
            unitPrice: priceOf(byType(entries, "write")),
          })
        );
      }
      if (read > 0) {
        items.push(
          lineItem({
            label: "Read operations",
            detail: `${Number(read).toLocaleString()} requests`,
            quantity: read / 1000,
            unit: "kOPS",
            unitPrice: priceOf(byType(entries, "read")),
          })
        );
      }

      if (!items.length) return null;

      return estimateLine({
        key: "storage.s3.standard",
        serviceId: "storage",
        name: "S3 Standard",
        description: "Object storage",
        config: { storage, write, read },
        items,
      });
    },

    backupLine() {
      const { enabled, storage, dr, drAmount } = this.backup;
      if (!enabled) return null;

      const entries = this.section("storage.s3.backup");
      const items = [];

      if (storage > 0) {
        items.push(
          lineItem({
            label: "Backup storage",
            detail: `${round(storage)} TB`,
            quantity: storage,
            unit: "TB/month",
            unitPrice: priceOf(byType(entries, "storage")),
          })
        );
      }
      if (dr && drAmount > 0) {
        items.push(
          lineItem({
            label: "Disaster recovery backup",
            detail: `${round(drAmount)} GB`,
            quantity: drAmount,
            unit: "GB/month",
            unitPrice: priceOf(byType(entries, "drbackup")),
          })
        );
      }

      if (!items.length) return null;

      return estimateLine({
        key: "storage.s3.backup",
        serviceId: "storage",
        name: "S3 Backup",
        description: "Backup storage",
        config: { storage, dr, drAmount },
        items,
      });
    },

    ltoLine() {
      const { enabled, plan, storage, retrieval } = this.lto;
      if (!enabled || !plan) return null;

      const items = [];
      const tb = Math.max(0, Number(storage) || 0);

      if (tb > 0) {
        if (plan.type === "worm") {
          const increments = Math.ceil(tb / WORM_INCREMENT_TB);
          items.push(
            lineItem({
              label: "LTO WORM archive",
              detail: `${round(tb)} TB → ${increments} × ${WORM_INCREMENT_TB} TB volume(s)`,
              quantity: increments,
              unit: "12TB/month",
              unitPrice: priceOf(plan),
            })
          );
        } else {
          items.push(
            lineItem({
              label: `LTO archive (${plan.type})`,
              detail: `${round(tb)} TB`,
              quantity: tb,
              unit: "TB/month",
              unitPrice: priceOf(plan),
            })
          );
        }
      }

      const retrieved = Math.max(0, Number(retrieval) || 0);
      if (retrieved > 0) {
        items.push(
          lineItem({
            label: "File retrieval",
            detail: `${round(retrieved)} GB restored`,
            quantity: retrieved,
            unit: "GB",
            unitPrice: priceOf(byType(this.section("storage.lto"), "fileretrieval")),
          })
        );
      }

      if (!items.length) return null;

      return estimateLine({
        key: "storage.lto",
        serviceId: "storage",
        name: "Storage LTO",
        description: plan.description || "Long-term archive",
        config: { type: plan.type, storage: tb, retrieval: retrieved },
        items,
      });
    },

    blockLine() {
      const { enabled, storage } = this.block;
      if (!enabled || storage <= 0) return null;

      return estimateLine({
        key: "storage.block",
        serviceId: "storage",
        name: "Block Storage",
        description: "Persistent volumes",
        config: { storage },
        items: [
          lineItem({
            label: "Block storage",
            detail: `${round(storage)} GB`,
            quantity: storage,
            unit: "GB/month",
            unitPrice: priceOf(byType(this.section("storage.block"), "standard")),
          }),
        ],
      });
    },

    registryLine() {
      const { enabled, storage } = this.registry;
      if (!enabled || storage <= 0) return null;

      return estimateLine({
        key: "storage.containerRegistry",
        serviceId: "storage",
        name: "Container Registry",
        description: "Container image storage",
        config: { storage },
        items: [
          lineItem({
            label: "Registry storage",
            detail: `${round(storage)} GB`,
            quantity: storage,
            unit: "GB/month",
            unitPrice: priceOf(byType(this.section("storage.containerRegistry"), "standard")),
          }),
        ],
      });
    },

    efsLine() {
      if (!this.efs.enabled || !this.efsEntry) return null;

      return estimateLine({
        key: "storage.elasticFileSystem",
        serviceId: "storage",
        name: "Elastic File System",
        description: this.efsEntry.description,
        config: { type: this.efsEntry.type },
        items: [],
        onRequest: true,
      });
    },

    lines() {
      return [
        this.standardLine,
        this.backupLine,
        this.ltoLine,
        this.blockLine,
        this.registryLine,
        this.efsLine,
      ].filter((line) => line && (line.monthlyCost > 0 || line.onRequest));
    },
  },
  watch: {
    ltoPlans: {
      immediate: true,
      handler(list) {
        if (!this.lto.plan && list.length) this.lto.plan = list[0];
      },
    },
  },
  methods: {
    cost(line) {
      return line ? line.monthlyCost : 0;
    },
    hint(path, type) {
      const entry = byType(this.section(path), type);
      if (!entry) return "";
      const price = entry.price === null ? "on request" : `${entry.price} € per ${entry.unit}`;
      return `${entry.description || ""} — ${price}`.trim();
    },
  },
};
</script>
